<template>
  <form class="form" @sumbit.prevent>
    <div class="form__content">
      <h1 class="extra-large-headline">
        Source of funds questionnaire for politically exposed persons
      </h1>

      <div class="question">
        <h3 class="headline">What is source of funds?</h3>
        <p class="bodycopy">
          Source of funds basically explains where the “money” you have used and
          will be using to fund transactions. This is to ensure that your funds
          are from legitimate sources.
        </p>
      </div>
      <div class="question">
        <h3 class="headline">Why am I receiving this questionnaire?</h3>
        <p class="bodycopy">
          You are receiving this questionnaire because you or an immediate
          family member of yours have been identified as a politically exposed
          person.
        </p>
      </div>
      <div class="question">
        <h3 class="headline">What is a politically exposed person (PEP)?</h3>
        <p class="bodycopy">
          A PEP is an individual entrusted with prominent public position or
          function for example a role in the goverment body, a state owned
          enterprise or an international organisation. Immediate family members
          and close associates of these individuals are also considered PEPs.
          The SoF questionaire is mandatory, so please answer ALL questions and
          provide the requested supporting documents.
        </p>
      </div>
    </div>
    <form @submit.prevent>
      <AutocompleteSelect
        v-model:autocomplete="form.country"
        :label="'Country of residence*'"
        :placeholder="'Search a country'"
        :type="'text'"
        :items="form.countryList"
      />

      <section>
        <p class="form__question-aide small-headline">
          Do you have dual citizenship?
        </p>

        <div class="form__pep-buttons">
          <ButtonBase
            @onButtonClick="toggleDualCitizenship(true)"
            class="button-outlined"
            :class="{ '--is-selected': dualCitizenship === true }"
          >
            YES
          </ButtonBase>
          <ButtonBase
            @onButtonClick="toggleDualCitizenship(false)"
            class="button-outlined"
            :class="{ '--is-selected': dualCitizenship === false }"
          >
            NO
          </ButtonBase>
        </div>

        <FormSelect
          v-show="dualCitizenship"
          type="text"
          name="citizenshipCountries"
          label="Please select your dual citizenship countries:"
          :maxItems="2"
          isMultiSelect
          :items="form.countryList"
          @dropdownValueUpdate="updateDropdown"
        />
      </section>

      <section v-show="isADefinedBoolean(dualCitizenship)">
        <p class="form__question-aide small-headline">
          Is your PEP status or your family member's PEP status related to your
          employment?
        </p>

        <div class="form__pep-buttons">
          <ButtonBase
            @onButtonClick="togglePepRelatedByEmploymentForm(true)"
            class="button-outlined"
            :class="{ '--is-selected': pepRelatedByEmployment === true }"
          >
            YES
          </ButtonBase>
          <ButtonBase
            @onButtonClick="togglePepRelatedByEmploymentForm(false)"
            class="button-outlined"
            :class="{ '--is-selected': pepRelatedByEmployment === false }"
          >
            NO
          </ButtonBase>
        </div>
      </section>

      <!-- pep / family member is a pep - related to employment -->
      <Transition name="fade" mode="out-in">
        <section v-show="pepRelatedByEmployment">
          <p class="form__question-aide">
            These questions are applicable when you pep status is related to
            your employment.
          </p>

          <BaseInput
            name="jobTitle"
            label="Your job title"
            placeholder="Enter your job title"
            @inputValueUpdate="updateInput"
          />

          <BaseInput
            name="organization"
            label="The Name of Your Employer Or Organization:"
            placeholder="Enter your industry"
            @inputValueUpdate="updateInput"
          />

          <BaseInput
            name="industryType"
            label="Relationship with PEP (applicable if you are a family member):"
            placeholder="Enter your industry"
            @inputValueUpdate="updateInput"
          />
        </section>
      </Transition>

      <!-- pep / family member is a pep - related NOT to employment -->
      <Transition name="fade" mode="out-in">
        <section v-show="pepRelatedByEmployment === false">
          <p class="form__question-aide">
            These questions are applicable when you pep status is
            <strong>not</strong> related to your employment.
          </p>

          <FormSelect
            name="employmentStatus"
            label="Choose your employment status:"
            type="text"
            :items="pepFormAnswers.employmentStatus"
            @dropdownValueUpdate="updateDropdown"
          />

          <BaseInput
            v-if="
              form.employmentStatus === 'Employed' ||
              form.employmentStatus === 'Self-employed'
            "
            name="jobTitle"
            label="Your Job Title:"
            placeholder="Enter your job title"
            @inputValueUpdate="updateInput"
          />

          <BaseInput
            name="organization"
            label="The Name of Your Employer Or Organization:"
            placeholder="Enter your industry"
            @inputValueUpdate="updateInput"
          />
        </section>
      </Transition>
      <Transition name="fade" mode="out-in">
        <section v-show="isADefinedBoolean(pepRelatedByEmployment)">
          <FormSelect
            name="industryType"
            label="Describe the type of industry:"
            type="text"
            :items="pepFormAnswers.industryType"
            @dropdownValueUpdate="updateDropdown"
          />

          <FormSelect
            name="yearlyAmount"
            label="How much cryptocurrency do you anticipate buying per year using our service?"
            type="text"
            :allowSort="false"
            :items="pepFormAnswers.yearlyAmount"
            @dropdownValueUpdate="updateDropdown"
          />

          <FormSelect
            name="potMainPurpose"
            label="Describe the purpose of your transactions."
            type="text"
            :items="pepFormAnswers.potMainPurpose"
            @dropdownValueUpdate="updateDropdown"
          />

          <FormSelect
            name="originOfFunds"
            label="Describe the origin of the financial resources you have used and will be using to conduct transactions with us: "
            type="text"
            :items="pepFormAnswers.originOfFunds"
            @dropdownValueUpdate="updateDropdown"
          />

          <Checkbox
            name="tnc"
            text="I declare that the information I have provided on the Source of Funds Questionnaire is true and complete. I also agree to provide supporting documents as proof of funds upon request by Banxa."
            v-model:value="form.tnc"
            v-model:tnc="form.tnc"
            :errors="pepSourceOfFundsStore.getErrorByField('tnc')"
          />

          <ButtonBase
            :isDisabled="
              pepSourceOfFundsStore.formHasErrors ||
              globalStore.getIsFormLoading
            "
            :isLoading="globalStore.getIsFormLoading"
            @onButtonClick="handleFormSubmit"
          >
            Continue
          </ButtonBase>
        </section>
      </Transition>
    </form>
  </form>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import { useGlobalStore } from "@/stores/globalStore";
import { usePepSourceOfFundsFormStore } from "@/stores/forms/pepSourceOfFundsFormStore";
import { useVerificationStore } from "@/stores/forms/verificationFormStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";
import { useBuyOrderFormStore } from "@/stores/forms/buyOrderFormStore";

import useApiHelpers from "@/composables/api/helpers";
import useCustomerActions from "@/composables/api/useCustomerActions";
import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useGlobalCountriesActions from "@/composables/store/global/useGlobalCountriesActions";

import FormSelect from "@/components/ui/selects/FormSelect.vue";
import Checkbox from "@/components/ui/inputs/Checkbox.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import AutocompleteSelect from "@/components/ui/selects/AutocompleteSelect.vue";
import BaseInput from "@/components/ui/inputs/BaseInput.vue";

const router = useRouter();
const globalStore = useGlobalStore();
const pepSourceOfFundsStore = usePepSourceOfFundsFormStore();
const verificationStore = useVerificationStore();
const { getNextIdentityRoute } = useIdentityQueueStore();
const { postRequest } = useApiHelpers();
const { fetchCustomerId } = useCustomerActions();
const { watchAndUpdateStore } = useWatchAndUpdate();
const { getRestrictedCountriesByActiveCoin } = useBuyOrderFormStore();
const { fetchCountries } = useGlobalCountriesActions();

// todo mocked for now, to get from an endpoint
const pepFormAnswers = ref({
  yearlyAmount: [
    "Up to 15,000",
    "15,000 to 30,000",
    "30,000 to 50,000",
    "50,000 to 70,000",
    "70,000 to 100,000",
    "100,000 and more",
  ],
  employmentStatus: [
    "Self-employed",
    "Employed",
    "Retired",
    "Unemployed",
    "Full time students",
  ],
  potMainPurpose: [
    "Investment",
    "Trading",
    "Payment for online purchases",
    "Payment for services",
    "Gaming",
    "Other",
    "Ransomware / virus/ cyber extortion demands",
  ],
  industryType: [
    "Agriculture, Food and Natural Resources",
    "Construction",
    "Architecture",
    "Arts, Audio/Video Technology and Communications",
    "Business Management and Adminstration",
    "Education and Training",
    "Finance",
    "Government and Public Adminstration",
    "Health and Science (eg. medical personnel",
    "Hospitality and Tourism",
    "Human Services (eg. social workers)",
    "Information Technology",
    "Law, Public Safety, Connections and Security",
    "Manufacturing",
    "Marketing, Sales and Service",
    "Non-Governmental Organistion/Charity",
    "Professional Service Provider/Consulting",
    "Real Estate",
    "Science, Technology, Engineering and Mathematics",
  ],
  originOfFunds: [
    "Personal Savings",
    "Salary",
    "Business Income/Profits",
    "Pension",
    "Givernment Benefits",
    "Income from Cryptocurrency Trading",
    "Income from Investments (eg. shares, bonds, investment funds)",
    "Income from sale of real estate or other property",
    "Income from renting/leasing",
    "Private loan from family members / friends / others",
    "Loan from bank",
    "Inheritance",
    "Gift",
    "Other",
  ],
});

const form = ref({
  countryList: [],
  country: pepSourceOfFundsStore.form.country.value,
  citizenshipCountries: pepSourceOfFundsStore.form.citizenshipCountries.value,
  employmentStatus: pepSourceOfFundsStore.form.employmentStatus.value,
  industryType: pepSourceOfFundsStore.form.industryType.value,
  jobTitle: pepSourceOfFundsStore.form.jobTitle.value,
  organization: pepSourceOfFundsStore.form.organization.value,
  yearlyAmount: pepSourceOfFundsStore.form.yearlyAmount.value,
  potMainPurpose: pepSourceOfFundsStore.form.potMainPurpose.value,
  originOfFunds: pepSourceOfFundsStore.form.originOfFunds.value,
  tnc: pepSourceOfFundsStore.form.tnc.value || false,
});
const dualCitizenship = ref(null);
const pepRelatedByEmployment = ref(null);

const updateDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  form.value[field] = value;
};

const handleFormSubmit = async () => {
  const custId = await fetchCustomerId();
  const countryCode = globalStore.getCountryCode(form.value.country);

  const formData = {
    documentType: "pep",
    documentReference: custId,
    declaredPepSourceOfFunds: {
      country_of_residence: countryCode,
      countries_of_citizenship: form.value.citizenshipCountries,
      employment_status: form.value.employmentStatus,
      employment_industry: form.value.industryType,
      job_title: form.value.jobTitle,
      organization: form.value.organization,
      expected_amount: form.value.yearlyAmount,
      purpose_of_transaction: [form.value.potMainPurpose],
      origin_of_funds: [form.value.originOfFunds],
    },
  };

  globalStore.setIsFormLoading(true);
  postRequest("documents/edd", formData, () =>
    router.push(getNextIdentityRoute)
  );
};

const updateInput = (data) => {
  const field = Object.keys(data)[0];
  const value = Object.values(data)[0];
  form.value[field] = value;
};

const togglePepRelatedByEmploymentForm = (isPepRelatedByEmployment) => {
  pepRelatedByEmployment.value = isPepRelatedByEmployment;
};

const toggleDualCitizenship = (isDualCitizenShip) => {
  dualCitizenship.value = isDualCitizenShip;
};

const isADefinedBoolean = (variableToCheck) => {
  return variableToCheck === true || variableToCheck === false;
};

onMounted(async () => {
  const formattedCountries = await fetchCountries();
  form.value.countryList = formattedCountries;
});
watchAndUpdateStore(form, pepSourceOfFundsStore, () => {});
</script>

<style lang="scss" scoped>
.question {
  &__title {
    font-size: 1rem;
  }
}

.form {
  &__question-aide {
    padding-top: $padding-sml;
    padding-bottom: $padding-sml;
  }

  &__pep-buttons {
    display: flex;
    flex-direction: row;
    margin-bottom: $padding-sml;
  }

  .button-outlined {
    margin-inline: calc($padding-sml/2);
    background: transparent;
    border: 1px solid var(--primary);

    &.--is-selected {
      background: var(--primary);
    }

    &:hover {
      background: var(--primary);
    }
  }
}
</style>
